import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { TaskResultResponse } from "@/clients/models/TaskResultResponse";
import { MealsService } from "@/clients/services/MealsService";
import { QueryKeys } from "@/constants/query-keys";
import {
  RECIPE_PROCESSING_SOURCE_ADD_TO_MEAL_TIME,
  RECIPE_PROCESSING_SOURCE_EXTRA_FOOD,
} from "@/features/meal-plan/constants/extra-food";
import { useAddExtraMeal } from "@/hooks/requests/plan/use-add-extra-meal";
import { useAddMealSlot } from "@/hooks/requests/plan/use-add-meal-slot";
import { useToast } from "@/hooks/ui/use-toast";
import { MealPlan } from "@/pages/plans/types/meal-plan.type";
import { planAdjustmentIntentAtom } from "@/stores/plan-adjustment-modal.store";
import { guardGetUserFromLocalStorage } from "@/utils/request";
import { useIonRouter } from "@ionic/react";
import { addBreadcrumb, captureException } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { setPlanDetailMealData } from "../use-meal-detail-data";

export interface RecipeProcessingErrorState {
  title: string;
  subtitle: string;
  rawMessage: string;
}

interface UseHandleRecipeProcessingResultProps {
  mealId: string | null;
  /** Task result from your-food status API; error_message may be string | null. */
  statusData?: TaskResultResponse;
  isError: boolean;
  source?: string | null;
  day?: string | null;
  targetDate?: string | null;
  mealTime?: string | null;
}

const findMealByIdInYourFood = (
  mealId: string,
  yourFood: {
    breakfast?: Meal[];
    lunch?: Meal[];
    dinner?: Meal[];
    snack?: Meal[];
  },
): { meal: Meal; mealTime: MealTime } | null => {
  const mealTimeToMeals: Array<{ mealTime: MealTime; meals: Meal[] | undefined }> = [
    { mealTime: MealTime.BREAKFAST, meals: yourFood.breakfast },
    { mealTime: MealTime.LUNCH, meals: yourFood.lunch },
    { mealTime: MealTime.DINNER, meals: yourFood.dinner },
    { mealTime: MealTime.SNACK, meals: yourFood.snack },
  ];

  for (const { mealTime, meals } of mealTimeToMeals) {
    const meal = meals?.find((item) => item.id === mealId);
    if (meal) {
      return { meal, mealTime };
    }
  }

  return null;
};

export const useHandleRecipeProcessingResult = ({
  mealId,
  statusData,
  isError,
  source,
  day,
  targetDate,
  mealTime,
}: UseHandleRecipeProcessingResultProps) => {
  const router = useIonRouter();
  const queryClient = useQueryClient();
  const { showSuccess } = useToast();
  const { addExtraMealAsync } = useAddExtraMeal();
  const { addMealSlotAsync } = useAddMealSlot();
  const setPlanAdjustmentIntent = useSetAtom(planAdjustmentIntentAtom);
  const handledMealIdRef = useRef<string | null>(null);
  const [errorState, setErrorState] = useState<RecipeProcessingErrorState | null>(null);
  const isExtraFoodSource = source === RECIPE_PROCESSING_SOURCE_EXTRA_FOOD;
  const isAddToMealTimeSource = source === RECIPE_PROCESSING_SOURCE_ADD_TO_MEAL_TIME;

  useEffect(() => {
    setErrorState(null);
  }, [mealId]);

  useEffect(() => {
    const handleSuccess = async () => {
      if (statusData?.status !== "completed" || !mealId || handledMealIdRef.current === mealId) {
        return;
      }
      handledMealIdRef.current = mealId;

      try {
        showSuccess("Your food added successfully");
        const user = guardGetUserFromLocalStorage();
        queryClient.invalidateQueries({ queryKey: [QueryKeys.YOUR_FOOD] });
        queryClient.invalidateQueries({ queryKey: [QueryKeys.MY_FOODS] });
        const yourFood = await queryClient.fetchQuery({
          queryKey: [QueryKeys.YOUR_FOOD],
          queryFn: async () => {
            const { data } = await MealsService.getYourFoodApiV1MealsYourFoodGet(user.id);
            return data;
          },
        });

        const matched = findMealByIdInYourFood(mealId, yourFood);
        if (!matched) {
          addBreadcrumb({
            message: "recipe_processing.navigating",
            data: { mealId, destination: "/plans/my-plan", reason: "meal_not_matched" },
          });
          router.push("/plans/my-plan", "forward", "replace");
          return;
        }

        if (isExtraFoodSource) {
          try {
            const normalizedDay =
              (
                day ??
                new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                })
              )?.toLowerCase() ?? "monday";

            await addExtraMealAsync({
              day: normalizedDay,
              meal_id: matched.meal.id ?? mealId,
              meal_name: matched.meal.name ?? "Extra Food",
              targetDate: targetDate ?? undefined,
            });

            queryClient.invalidateQueries({ queryKey: [QueryKeys.PLAN] });

            const myPlanSearch = new URLSearchParams();
            if (day) {
              myPlanSearch.set("day", day);
            }
            if (targetDate) {
              myPlanSearch.set("date", targetDate);
            }

            const myPlanPath = myPlanSearch.toString()
              ? `/plans/my-plan?${myPlanSearch.toString()}`
              : "/plans/my-plan";
            addBreadcrumb({
              message: "recipe_processing.navigating",
              data: { mealId, destination: myPlanPath, flow: "extra_food" },
            });
            router.push(myPlanPath, "forward", "replace");
          } catch (error) {
            const rawMessage = error instanceof Error ? error.message : "Failed to add extra food.";
            setErrorState({
              title: "Something went wrong",
              subtitle: "Failed to add food into Extra Food. Please try again.",
              rawMessage,
            });
          }
          return;
        }

        if (isAddToMealTimeSource) {
          const normalizedDay =
            (
              day ??
              new Date().toLocaleDateString("en-US", {
                weekday: "long",
              })
            )?.toLowerCase() ?? "monday";
          const resolvedMealTime = (mealTime ?? matched.mealTime) as MealTime;

          const myPlanSearch = new URLSearchParams();
          if (day) {
            myPlanSearch.set("day", day);
          }
          if (targetDate) {
            myPlanSearch.set("date", targetDate);
          }
          const myPlanPath = myPlanSearch.toString()
            ? `/plans/my-plan?${myPlanSearch.toString()}`
            : "/plans/my-plan";

          try {
            const resolvedMealId = matched.meal.id ?? mealId;
            const result = (await addMealSlotAsync({
              day: normalizedDay,
              mealTime: resolvedMealTime,
              meal_id: resolvedMealId,
              meal_name: matched.meal.name ?? undefined,
              targetDate: targetDate ?? undefined,
            })) as Record<string, unknown> | undefined;

            queryClient.invalidateQueries({ queryKey: [QueryKeys.PLAN] });

            if (result?.needs_adjustment) {
              setPlanAdjustmentIntent({
                kind: "add-meal-to-slot",
                day: normalizedDay,
                mealTime: resolvedMealTime,
                mealId: resolvedMealId,
                mealName: matched.meal.name ?? undefined,
                targetDate: targetDate ?? undefined,
                canUndo: Boolean(result?.is_undoable),
              });
            }

            addBreadcrumb({
              message: "recipe_processing.navigating",
              data: { mealId, destination: myPlanPath, flow: "add_to_meal_time" },
            });
            router.push(myPlanPath, "forward", "replace");
          } catch (error) {
            const rawMessage =
              error instanceof Error ? error.message : "Failed to add meal to the plan.";
            captureException(error, {
              extra: { mealId, source, flow: "add_to_meal_time" },
            });
            setErrorState({
              title: "Something went wrong",
              subtitle: "We couldn't add the meal to your plan. Please try again.",
              rawMessage,
            });
          }
          return;
        }

        const mealPlan: MealPlan = {
          meal: matched.meal,
          mealTime: matched.mealTime,
          mealDay: new Date().toLocaleDateString("en-US", { weekday: "long" }).toLowerCase(),
          mealIndex: 0,
          fromSavedMeals: true,
        };
        setPlanDetailMealData(queryClient, mealPlan);

        addBreadcrumb({
          message: "recipe_processing.navigating",
          data: { mealId, destination: "/plans/my-plan -> /plans/meal-detail" },
        });
        router.push("/plans/my-plan", "forward");
        setTimeout(() => {
          router.push("/plans/meal-detail", "forward", "push");
        }, 1000);
      } catch (error) {
        captureException(error, {
          extra: { mealId, source, flow: "recipe_processing" },
        });
      }
    };

    void handleSuccess();
  }, [
    statusData?.status,
    mealId,
    queryClient,
    router,
    showSuccess,
    isExtraFoodSource,
    isAddToMealTimeSource,
    addExtraMealAsync,
    addMealSlotAsync,
    setPlanAdjustmentIntent,
    day,
    targetDate,
    mealTime,
    source,
  ]);

  useEffect(() => {
    if (isError || statusData?.status === "failed") {
      const rawMessage = statusData?.error_message ?? "Something went wrong. Please try again.";

      const isMissingNutritionalData = rawMessage.includes("missing required nutritional data");

      setErrorState({
        title: isMissingNutritionalData
          ? "Oops, we couldn't add that food"
          : "Something went wrong",
        subtitle: isMissingNutritionalData
          ? "This is usually because the your food link doesn't have the ingredients."
          : "Please try again.",
        rawMessage,
      });
    }
  }, [isError, statusData?.status, statusData?.error_message]);

  return { errorState, router };
};
